import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronDown, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { readScreenCache, writeScreenCache } from '../lib/screenDataCache';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import StickyBackPill from '../components/StickyBackPill';
import GroupsLoadError from '../components/GroupsLoadError';
import CirculoPalomita from '../components/CirculoPalomita';
// t7-2b (E3, pendiente 13): el glifo del tipo en la cabecera, sin caja.
import { GlifoDeMaterial } from '../components/IconoDeMaterial';
import { tipoDeMaterial } from '../lib/tipoDeMaterial';
// TANDA 26 (firma #234): de los dos módulos de copys ya sólo salen FUNCIONES.
// Las constantes planas —`COPYS.*`, `PUBLICACION.*`, los dos chips, la línea
// condicional del modal— viven ahora en `dashboard:groups.bank.*` y se leen aquí
// con `t()`. Lo que se quedó en los módulos es la GRAMÁTICA: singulares,
// plurales, y las dos formas del rango de páginas.
import {
  conteoDePreguntas, danioParcial, etiquetaPaginasDanadas, rangoDelTema,
  renglonDeTema, sePuedeMarcar, textoDelChip,
} from '../lib/temasCopys';
import {
  bannerPublicacionCreada, cuerpoDePublicacion, renglonDeTemaPublicable,
} from '../lib/curacionCopys';
import { avisosDeTransicion } from '../lib/bannersDeTema';
import { sacarBanners } from '../lib/bannerDeLaCasa';
// TANDA 26b (#236): la función hermana de la que despierta al vigilante tras una
// subida. Esta pantalla no conoce al chasis — la función encapsula un evento de
// `window`, igual que allá.
import { registrarTemaEncolado } from '../components/GroupBankVigilante';
import {
  CAPA, CAPA_TOCABLE, ETIQUETA, META, RELLENO_CAPA, RITMO_LISTA, RITMO_SECCION,
  TITULO_PANTALLA, TITULO_SECCION,
  CAPA_DE_SECCION, CABECERA_DE_SECCION, CHIP_DE_CONTEO, CUERPO_DE_SECCION, GUIA_DE_SECCION,
  TITULO_DE_SECCION_PLEGABLE,
} from '../lib/vestidoDelCarril';
// t7-1b (T6): la fecha de caducidad de una card, en el MISMO modulo que usa el Panel
// del alumno (formato, inicio del dia en la zona del navegador y el minimo "manana").
import {
  fechaDeCaducidadDe, inicioDelDiaLocalISO, mananaLocal, valorDelCalendario,
  zonaDelNavegador,
} from '../lib/fechaDeCaducidad';

/* PIPE-INST T4 (#118) + T4.5 (#120) + T5 (#122/#123) — LA PANTALLA DE TEMAS.
 *
 * DÓNDE VIVE: ruta `/groups/:groupId/bank/:docId`, dentro de `AppLayout` y detrás
 * de `<InstitutionalRoute>`. Misma doctrina que las otras pantallas de profesor:
 * el guard esconde y el servidor niega, y ninguno confía en el otro.
 *
 * ── LAS CUATRO DECISIONES DE PANTALLA (de #118, intactas) ───────────────────
 *
 * 1. EL TÍTULO ES EL NOMBRE DEL ARCHIVO. Un profesor llega aquí desde una lista
 *    de archivos; si la pantalla se llamara «Temas» tendría que recordar cuál
 *    abrió. El encabezado de la sección sí es fijo («Temas del documento»).
 *
 * 2. GENERAR ES UNA DECISIÓN, NO UN EFECTO DE SUBIR. Subir lee el documento y
 *    arma la lista; lo que cuesta dinero empieza cuando el profesor palomea y
 *    toca el botón. Por eso la pantalla no dispara nada al montarse.
 *
 * 3. EL AVISO DE PDF DAÑADO NO BLOQUEA. Es un banner fijo arriba de la lista, sin
 *    popup y sin deshabilitar nada.
 *
 * 4. TODOS LOS AVISOS DE ESTE DOCUMENTO VIVEN EN ESTA PANTALLA. Ni push ni
 *    correos: el profesor sale, vuelve, y el avance está aquí.
 *
 * ── LO QUE T5 CAMBIA, Y ES LO GORDO ────────────────────────────────────────
 *
 * **MUERE EL LETRERO PERSISTENTE (#122).** Abría la sección con «Generación
 * terminada: 2/4 temas listos…» y seguía ahí al día siguiente, describiendo la
 * generación de ayer como si acabara de pasar. Lo sustituyen AVISOS MOMENTÁNEOS
 * por tema: el diff entre dos fotos del sondeo (`lib/bannersDeTema.js`) dice qué
 * temas acaban de terminar, y cada uno sale como un toast de la casa que se va
 * solo. Quien llega tarde no ve nada — los chips ya cuentan la historia.
 *
 * **NACEN LA CURACIÓN Y LA PUBLICACIÓN (#123).** Cada tema con material lleva su
 * botón «Revisar preguntas».
 *
 * ── LO QUE T5.2 CAMBIA (firmas #133-#136) ──────────────────────────────────
 *
 * **REVISAR DEJA DE SER UN PEAJE PARA PUBLICAR.** El botón «Publicar» vive
 * SIEMPRE junto a «Generar preguntas» —arriba y abajo de la lista— y se apaga
 * con su porqué cuando ningún tema tiene preguntas. El modal enseña TODOS los
 * temas con material: los que no han salido arriba y premarcados, los ya
 * publicados abajo con su tag, para poder armar una card nueva con material que
 * los alumnos ya tienen (el REMIX). Y la card entera marca su casilla: el
 * blanco táctil deja de ser un cuadradito de 16 px.
 *
 * ── EL SONDEO, Y POR QUÉ SE APAGA SOLO ──────────────────────────────────────
 *
 * Mientras haya un tema moviéndose —o el documento se esté leyendo— se relee el
 * estado cada 4 s; cuando nada se mueve, el intervalo muere. El intervalo NACE de
 * una condición y no se apaga desde dentro de un callback, que es como quedan
 * colgados.
 *
 * ── SUPERFICIE DE UN SOLO IDIOMA ────────────────────────────────────────────
 *
 * Cero `t()` y cero strings en inglés: todo el copy sale de `lib/temasCopys.js` y
 * `lib/curacionCopys.js`, donde sendas pruebas lo fijan verbatim.
 */

const POLL_MS = 4000;

/* TANDA 26b (#237) — LAS DOS CLAVES DE LOS AVISOS QUE ANTES IBAN AL PIE.
 *
 * En `sacarBanners` la clave es la IDENTIDAD del toast: dos avisos con la misma
 * clave no se apilan, el segundo reemplaza al primero. Aquí eso es exactamente lo
 * que se quiere — tocar «Generar preguntas» tres veces sin marcar nada deja UN
 * renglón, no tres. Son constantes y no literales sueltos porque las mira la
 * suite: si cambian, tiene que cambiar en un solo sitio.
 *
 * No llevan `tema_id` a propósito: no hablan de un tema, hablan del disparo. */
const CLAVE_SIN_SELECCION = 'temas-sin-seleccion';
const CLAVE_FALLO_GENERAR = 'temas-fallo-generar';

/* INFRA-2 (#365) — LOS CINCO AVISOS DEL BOTÓN «GENERAR», POR CÓDIGO.
 *
 * Son las cinco puertas de `_disparar` en el servidor, en el orden en que él las
 * mira. `sin_temas` REUSA el texto que el botón ya saca localmente cuando nadie
 * marcó nada (`no_selection`): es el mismo hecho contado desde los dos lados —la
 * pantalla lo corta antes, el servidor lo corta cuando la pantalla venía vieja—
 * y darle dos frases distintas al mismo hecho es exactamente lo que esta
 * superficie existe para no hacer.
 *
 * Un código que no esté aquí no pinta nada: la regla de la casa es que si el
 * servidor no dijo algo legible, la pantalla no se lo inventa — relee y enseña
 * el estado real. */
const AVISO_POR_CODIGO = {
  documento_demo: 'groups.bank.temas.err_documento_demo',
  sin_temas: 'groups.bank.temas.no_selection',
  TEMA_PUBLICADO: 'groups.bank.temas.err_tema_publicado',
  tema_desconocido: 'groups.bank.temas.err_tema_desconocido',
  tema_en_curso: 'groups.bank.temas.err_tema_en_curso',
  // ARREGLOS-PROFE (#379): el fallo mudo de «Publicar» gana su aviso; `publicar` cierra
  // el modal en cuanto `mostrarFallo` dice algo (como con los cinco de #365).
  tema_no_publicable: 'groups.bank.temas.err_tema_no_publicable',
  // t7-1b (T6): la fecha de caducidad que el servidor no acepta (400 del banco, en
  // publicar y en "Cambiar fecha"). Mismo canal y mismo sitio que los de arriba.
  caduca_invalida: 'groups.bank.temas.err_caduca_invalida',
};

/* t7-1b (T6) -- LAS PIEZAS DE LA CARD PUBLICADA: su nombre, su fecha y sus ventanas.
 *
 * EL TOPE DEL NOMBRE es el del servidor (`curacion.TOPE_DEL_NOMBRE`): el campo lo
 * impide y el servidor recorta sin error.
 *
 * LA VENTANA CHICA ES LA MISMA PIEZA QUE LA DE PUBLICAR: capa, panel y titulo llevan
 * EXACTAMENTE sus clases (las de publicar se quedan escritas en su JSX, sin tocar, y
 * `GroupBankTemasPage.cards.test.js` compara las dos listas clase por clase: si una
 * se mueve sin la otra, se pone roja).
 *
 * EL CAMPO es el `Input` de la casa vestido como el de crear grupo (`GroupsPage.js`),
 * su etiqueta es `ETIQUETA` (el nivel del carril para "la etiqueta que nombra un
 * campo") y su ayuda es la gris chica de las instrucciones de esta pagina. El de fecha
 * pide ademas el esquema oscuro: sin el, el icono del calendario sale negro sobre la
 * caja negra.
 *
 * EL BOTON DEL RENGLON es el vestido de "Revisar preguntas", el secundario de la
 * lista de temas: 44 px de blanco tactil en telefono y 32 de alto visual desde `sm`. */
const TOPE_DEL_NOMBRE = 80;
const VENTANA_CAPA = 'fixed top-14 inset-x-0 bottom-0 z-[60] bg-black/80 flex '
  + 'items-center justify-center p-4';
const VENTANA_PANEL = 'relative bg-[#111] border border-[#262626] rounded-[20px] '
  + 'p-6 sm:p-8 pb-[max(1.5rem,env(safe-area-inset-bottom,0px))] max-w-md '
  + 'w-full mx-4 shadow-2xl max-h-full overflow-y-auto';
const VENTANA_TITULO = 'text-lg font-semibold text-white mb-3';
const CAMPO = 'bg-[#0e0e0e] border-[#262626] text-white rounded-xl min-h-[44px]';
const CAMPO_FECHA = `${CAMPO} [color-scheme:dark]`;
const AYUDA = 'mt-1 text-xs text-[#9a9a9a]';
const BOTON_DE_RENGLON = 'border-[#3a3a3a] bg-[#161616] text-white rounded-lg h-8 px-3 '
  + 'text-[11px] font-medium min-h-[44px] sm:min-h-[32px] '
  + 'transition-[background-color,border-color] duration-150 '
  + 'hoverable:hover:bg-[#1f1f1f] hoverable:hover:border-[#4a4a4a]';
/* LA BOTONERA DE LAS VENTANAS CHICAS apila en columna bajo 640 px con el patron de
 * #459: el reparto a partes iguales va SOLO desde `sm`, porque en columna una base
 * de 0 hace que el navegador ignore el alto propio del boton. */
const BOTONERA = 'flex flex-col sm:flex-row gap-3';
const BOTON_CANCELAR = 'w-full sm:w-auto sm:flex-1 border-[#333] text-[#a0a0a0] '
  + 'rounded-xl min-h-[44px] text-sm';
const BOTON_GUARDAR = 'w-full sm:w-auto sm:flex-1 bg-[#FF6B00] hover:bg-[#E05A00] '
  + 'text-white rounded-xl min-h-[44px] text-sm disabled:opacity-40';

/* t7-3 remate 8 (#591, C131): EL FOCO NO SE SALE DE UNA VENTANA DE ESTA PAGINA.
 *
 * La casa no tiene una pieza de dialogo con trampa de foco para estas ventanas (son `div` fijos),
 * asi que cada una lleva este gancho: al abrir, el foco entra (si un campo con `autoFocus` ya lo
 * tomo, se queda ahi); Tab desde el ultimo control vuelve al primero y Shift+Tab desde el primero
 * va al ultimo (si el foco se habia ido fuera, Tab lo regresa adentro); Esc la cierra con la misma
 * funcion que el boton Cancelar; y al cerrar, el foco vuelve al control que la abrio (`volverA`),
 * si sigue en la pagina. El selector va sin espacios a proposito. */
const ENFOCABLES = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),'
  + 'textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/** Quien abrio la ventana: el control del clic (Safari no le da el foco a un boton al tocarlo) o, si
 *  no hay evento, el que tiene el foco. */
function quienAbrio(e) {
  return (e && e.currentTarget && typeof e.currentTarget.focus === 'function')
    ? e.currentTarget : document.activeElement;
}

function useFocoDeVentana(abierta, panelRef, alCerrar, volverA) {
  const cerrarRef = useRef(alCerrar);
  cerrarRef.current = alCerrar;
  useEffect(() => {
    if (!abierta) return undefined;
    const panel = panelRef.current;
    const enfocables = () => (panel ? Array.from(panel.querySelectorAll(ENFOCABLES)) : []);
    if (panel && !panel.contains(document.activeElement)) {
      const primero = enfocables()[0];
      if (primero) primero.focus();
    }
    const alTeclear = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (cerrarRef.current) cerrarRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;
      const lista = enfocables();
      if (lista.length === 0) { e.preventDefault(); return; }
      const primero = lista[0];
      const ultimo = lista[lista.length - 1];
      const dentro = panel.contains(document.activeElement);
      if (e.shiftKey) {
        if (!dentro || document.activeElement === primero) { e.preventDefault(); ultimo.focus(); }
      } else if (!dentro || document.activeElement === ultimo) {
        e.preventDefault();
        primero.focus();
      }
    };
    document.addEventListener('keydown', alTeclear);
    return () => {
      document.removeEventListener('keydown', alTeclear);
      const volver = volverA.current;
      volverA.current = null;
      if (volver && volver.isConnected && typeof volver.focus === 'function') volver.focus();
    };
  }, [abierta, panelRef, volverA]);
}

/** LA CARD EDITADA NO SE DEJA PISAR POR UNA FOTO VIEJA.
 *
 *  El sondeo de 4 s puede tener un GET en vuelo cuando vuelve el PATCH de una card;
 *  si ese GET llega DESPUES, traeria la card como estaba antes y la pantalla
 *  "deshaceria" lo que el profesor acaba de guardar hasta el siguiente tic. Cada
 *  lectura lleva su turno, y cada card editada recuerda el ultimo turno que YA habia
 *  salido cuando el servidor contesto: una foto de ese turno o anterior recibe la
 *  card editada; una pedida despues manda tal cual, porque ya la trae.
 *
 *  Sin cards editadas devuelve la MISMA foto, sin copiarla: todo lo de hoy sigue
 *  igual. */
function conCardsEditadas(foto, editadas, turno) {
  const lista = foto?.publicaciones;
  if (!Array.isArray(lista) || lista.length === 0) return foto;
  let cambio = false;
  const publicaciones = lista.map((card) => {
    const editada = editadas[card?.id];
    if (!editada || turno > editada.hasta) return card;
    cambio = true;
    return editada.card;
  });
  return cambio ? { ...foto, publicaciones } : foto;
}

/** La llave de la foto en memoria. Es por documento: dos documentos del mismo
 *  grupo no comparten pantalla. */
const claveDeCache = (groupId, docId) => `pi-temas:${groupId}:${docId}`;

/* ── LOS SIETE CHIPS ─────────────────────────────────────────────────────────
 *
 * El color y el texto hacen todo el trabajo: en la lista no entra ni un icono.
 * La receta (fondo 15% / texto 400 / borde 20%) es la del resto del producto —
 * Dashboard, StudyCenter y ManageDocuments la repiten verbatim.
 *
 * «Generando…» usa el naranja de la marca con el pulso ya ralentizado a 2.4 s del
 * mapa de partes: el de 2 s de Tailwind se lee nervioso. El pulso anima OPACIDAD,
 * no layout, así que no re-acomoda nada.
 *
 * T5 SUMA DOS, Y TIENEN QUE DISTINGUIRSE DE «LISTO PARA REVISAR» A GOLPE DE
 * VISTA: los tres son «buenos» y el profesor los va a leer en una lista de
 * veintitantos. Se quedan DENTRO del sistema de cinco colores de la casa —cero
 * paleta inventada— repartiéndose el verde y el azul del producto: «Listo para
 * revisar» conserva su verde esmeralda (hay trabajo que hacer), «Revisado» pasa a
 * un verde-azulado más frío (el trabajo está hecho) y «Publicado» toma el azul
 * de la casa con el borde marcado, que es el que lee «esto ya salió». (El `#k`
 * del chip murió en T5.2 — ver `CHIP_PUBLICADO` en `curacionCopys`.) */
const CHIP_BASE = 'inline-flex items-center gap-1.5 shrink-0 rounded-full border '
  + 'px-2 py-0.5 text-[11px] leading-4 whitespace-nowrap';
const CHIPS = {
  sin_generar: 'bg-[#151515] text-[#8a8a8a] border-[#2e2e2e]',
  en_fila: 'bg-[#1c1c1c] text-[#b4b4b4] border-[#3a3a3a]',
  generando: 'bg-[#FF6B00]/15 text-[#FF8c3e] border-[#FF6B00]/30 '
    + 'animate-pulse [animation-duration:2.4s]',
  listo_para_revisar: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  /* INFRA-2 (#363) — AMARILLO, Y BIEN VISIBLE.
   *
   * La firma lo pide explícito: «jamás gris ni un tono oscuro que no se note».
   * Un tema sin material es una decisión del modelo que el profesor TIENE que
   * ver —es lo que explica por qué esa parte del documento no tiene preguntas—,
   * así que no puede vestirse como «Sin generar» (gris apagado, `#8a8a8a`) ni
   * como «En fila» (gris claro). Es la MISMA familia `amber` de los dos banners
   * de daño de esta pantalla, que es lo que la casa ya usa para «esto no está
   * roto, pero míralo».
   *
   * Contraste medido sobre el fondo de la card: el texto `amber-300` (#fcd34d)
   * contra la capa (#151515 con el 15% de amber encima) da ~11:1 — muy por
   * encima del 4.5:1 de AA. El rojo de `no_se_pudo` no se toca. */
  sin_material: 'bg-amber-400/15 text-amber-300 border-amber-400/30',
  no_se_pudo: 'bg-red-500/15 text-red-400 border-red-500/20',
  revisado: 'bg-teal-500/15 text-teal-300 border-teal-500/25',
  publicado: 'bg-sky-500/15 text-sky-300 border-sky-500/40',
};

/* EL TEXTO DEL CHIP SE MUDÓ A `lib/temasCopys.js` EN LA TANDA 26 (firma #234).
 *
 * Vivía aquí porque componía tres constantes de dos módulos distintos; ahora es
 * una función de estado a llave, y las funciones de texto viven donde se prueban
 * sin montar la pantalla. Los siete estados siguen siendo los mismos y el mapa
 * de COLORES (`CHIPS`, arriba) no se toca: se indexa por el ESTADO, que es un
 * código, y no por el texto — que es justo la trampa que la curación sí tenía. */

/** LAS PREGUNTAS VIVAS DE UN TEMA: las que entrarían HOY a una card.
 *
 *  Pendientes + aprobadas. **Las descartadas no cuentan**, y es lo único que no
 *  se negocia de esta cuenta: el profesor que dijo «esta no» ya decidió, y
 *  prometerle a sus alumnos una pregunta que él rechazó sería lo contrario de lo
 *  que la curación existe para hacer.
 *
 *  Sobre un tema PUBLICADO la fórmula da exactamente las de su lock: sus
 *  pendientes son cero por construcción (publicar aprueba lo que se lleva) y sus
 *  aprobadas son las que salieron. Una sola cuenta para los dos casos. */
const vivas = (tema) =>
  (tema?.pendientes || 0) + (tema?.aprobadas || 0);

/** LA CASILLA SÓLO DONDE SIRVE. Un tema pedido, terminado o publicado no se
 *  vuelve a palomear desde aquí, así que no lleva casilla — ni gris ni apagada.
 *  Una casilla que no hace nada es ruido que el profesor tiene que aprender a
 *  ignorar. */
/* INFRA-2 (#363): `sin_material` entra a la lista, y eso es «la casilla igual
 * que hoy» — no una decisión nueva. Ese tema se pintaba `no_se_pudo`, que YA
 * llevaba casilla; separarlo en un estado propio sin dársela se la habría
 * quitado por un efecto colateral. Y tiene sentido: si el profesor cree que sí
 * había algo que preguntar ahí, lo vuelve a palomear. Lo que cambia es lo que
 * LEE antes de decidir, no lo que puede hacer. */
const CON_CASILLA = ['sin_generar', 'sin_material', 'no_se_pudo'];
const llevaCasilla = (tema) => CON_CASILLA.includes(tema?.estado);

/* mezcla (21-oct, firma de Carlos, opcion A): "Temas del documento" nace ABIERTA si
 * ningun tema tiene nada generado ni en vuelo (todos llevan casilla), y PLEGADA si
 * alguno esta en fila, generando, listo para revisar, revisado o publicado. `null`
 * mientras no hayan llegado temas: todavia no hay nada que decidir. */
export const temasNacenAbiertos = (temas) => (
  Array.isArray(temas) && temas.length > 0 ? temas.every(llevaCasilla) : null);

/** Los tres estados en los que un tema TIENE material que revisar. */
const CON_MATERIAL = ['listo_para_revisar', 'revisado', 'publicado'];

/** El esqueleto de la primera carga. Tres cards del alto real, para que la lista
 *  no brinque cuando llegue la de verdad. */
function EsqueletoDeTemas() {
  return (
    <div className="space-y-2" data-testid="temas-esqueleto">
      {[0, 1, 2].map((i) => (
        /* EL ESQUELETO SE VISTE COMO LO QUE VA A SUSTITUIR (T5.2): con la
           MISMA capa. Si no, la lista cambia de color al llegar la foto real —
           un parpadeo que se lee como si algo hubiera fallado. */
        <div key={i} className={`${CAPA} ${RELLENO_CAPA} space-y-2`}>
          <div className="flex items-center gap-2.5">
            <div className="h-3.5 flex-1 rounded animate-pulse bg-white/[0.06]" />
            <div className="h-4 w-24 rounded-full animate-pulse bg-white/[0.06]" />
          </div>
          <div className="h-3 w-20 rounded animate-pulse bg-white/[0.06]" />
        </div>
      ))}
    </div>
  );
}

export default function GroupBankTemasPage() {
  const { groupId, docId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['dashboard', 'common']);

  // EL TRADUCTOR EN UNA REF, EL MISMO PATRÓN DEL VIGILANTE
  // (`GroupBankVigilante.js:165`) Y DEL RECLAMO DE REFERIDO (`App.js:147`).
  // `cargar` usa `t` para redactar los avisos de transición, pero NO puede
  // depender de él: es una función que pide los temas al servidor y de la que
  // cuelgan el efecto de carga (`useEffect(() => { cargar(); }, [cargar])`) y el
  // sondeo de cuatro segundos.
  // Con `t` en sus dependencias, cambiar de idioma le daría identidad
  // nueva y la pantalla estrenaría una petición de más y un reinicio del
  // intervalo por un cambio que sólo afecta al TEXTO de un aviso. La ref da
  // siempre la traducción vigente EN EL INSTANTE en que el aviso sale —que
  // además es lo correcto— sin volver a atar la función.
  const tRef = useRef(t);
  tRef.current = t;

  // LA FOTO ARRANCA DE LA MEMORIA CUANDO LA HAY. Volver a un documento ya visto
  // pinta al instante lo último bueno y la relectura de abajo lo actualiza por
  // detrás. Es exactamente lo que esta pantalla YA hace cada 4 s por su cuenta.
  // (Se usa el cache del producto y no un Map propio para heredar su purga por
  // frontera de identidad — la lección de privacidad de F4.3b.)
  const [datos, setDatos] = useState(
    () => readScreenCache(claveDeCache(groupId, docId))?.data || null);
  const [noExiste, setNoExiste] = useState(false);
  // EL TERCER ESTADO, el de PILOTO-1C: «no se pudo leer» no es «no hay nada» ni
  // un esqueleto eterno. Sólo manda cuando NO hay una foto buena que enseñar.
  const [errorDeCarga, setErrorDeCarga] = useState(false);
  const [marcados, setMarcados] = useState({});  // {tema_id: true}
  const [enviando, setEnviando] = useState(false);
  const [trabajando, setTrabajando] = useState({});  // {tema_id: true} "generar más"
  const [modalPublicar, setModalPublicar] = useState(null);   // {temas: [...]}
  const [publicando, setPublicando] = useState(false);
  // t7-1b (T6): LA VENTANA CHICA DE UNA CARD PUBLICADA, `null` o
  // `{tipo: 'nombre'|'fecha', card, valor}` (`valor` es lo que dice su campo).
  const [ventanaDeCard, setVentanaDeCard] = useState(null);
  const [guardandoCard, setGuardandoCard] = useState(false);
  // t7-3 remate 8 (#591, C131): el foco de las dos ventanas (ver `useFocoDeVentana`). Esc cierra como
  // Cancelar: en la de publicar siempre; en la chica, no mientras guarda (su Cancelar esta apagado).
  const panelPublicarRef = useRef(null);
  const panelCardRef = useRef(null);
  const volverDePublicar = useRef(null);
  const volverDeCard = useRef(null);
  useFocoDeVentana(!!modalPublicar, panelPublicarRef, () => setModalPublicar(null), volverDePublicar);
  useFocoDeVentana(!!ventanaDeCard, panelCardRef,
    () => { if (!guardandoCard) setVentanaDeCard(null); }, volverDeCard);
  // series-7 (#484): LAS DOS SECCIONES, SIEMPRE PLEGADAS AL ENTRAR. El estado vive
  // solo en memoria y nace en `false` en cada montaje: volver a esta pantalla la
  // encuentra plegada otra vez. El sondeo de 4 s re-renderiza, no re-monta, asi
  // que tampoco la vuelve a plegar.
  // mezcla (21-oct, opcion A): "Temas del documento" ya no nace siempre plegada. Su
  // valor inicial se decide UNA vez por documento, cuando llegan sus temas por primera
  // vez (`temasNacenAbiertos`); `decididoPara` guarda de que documento es la decision,
  // asi los sondeos no la vuelven a decidir (si el profe genera el primer tema con la
  // seccion abierta, no se le pliega en la cara) y el toque del profe manda.
  // "Publicaciones" sigue como #484: plegada al entrar.
  const claveDelDoc = claveDeCache(groupId, docId);
  const claveActual = useRef(claveDelDoc);
  claveActual.current = claveDelDoc;
  const decididoPara = useRef(null);
  const [temasAbiertos, setTemasAbiertos] = useState(() => {
    const abrir = temasNacenAbiertos(datos?.temas);
    if (abrir !== null) decididoPara.current = claveDelDoc;
    return abrir === true;
  });
  const decidirTemasAlLlegar = useCallback((clave, temas) => {
    if (clave !== claveActual.current || decididoPara.current === clave) return;
    const abrir = temasNacenAbiertos(temas);
    if (abrir === null) return;
    decididoPara.current = clave;
    setTemasAbiertos(abrir);
  }, []);
  const [cardsAbiertas, setCardsAbiertas] = useState(false);

  // LA FOTO ANTERIOR, EN UNA REF Y NO EN ESTADO. Es el otro lado del diff que
  // produce los banners, y no se pinta: guardarla en estado provocaría un render
  // extra por cada tic del sondeo sin que nada de la pantalla cambiara.
  const fotoPrevia = useRef(null);
  // t7-1b (T6): EL TURNO DE CADA LECTURA Y LAS CARDS RECIEN EDITADAS, en refs por
  // lo mismo que la foto previa: no se pintan. Ver `conCardsEditadas`.
  const lecturas = useRef(0);
  const cardsEditadas = useRef({});

  const cargar = useCallback(async () => {
    const turno = ++lecturas.current;
    try {
      const res = await api.get(`/groups/${groupId}/bank/${docId}/temas`);
      // ── LOS AVISOS MOMENTÁNEOS (#122) ──────────────────────────────────────
      // Se calculan ANTES de guardar la foto nueva, que es lo único que hace
      // posible comparar. Salen EN FILA (la cola de la casa) y jamás encimados.
      sacarBanners(
        avisosDeTransicion(tRef.current, fotoPrevia.current, res.data?.temas || []));
      fotoPrevia.current = res.data?.temas || [];
      // t7-1b (T6): sin cards editadas, `foto` ES `res.data` (la misma referencia).
      const foto = conCardsEditadas(res.data, cardsEditadas.current, turno);
      setDatos(foto);
      decidirTemasAlLlegar(claveDeCache(groupId, docId), foto?.temas);
      // INFRA-2 (#364): LLEGÓ LA FOTO, MANDA EL SERVIDOR. El «En fila»
      // optimista dura exactamente lo que tarda esta lectura — ni un tic más.
      // Se limpia ENTERO y no por tema: la foto describe todos.
      setEnFilaOptimista(prev => (Object.keys(prev).length ? {} : prev));
      writeScreenCache(claveDeCache(groupId, docId), foto);
      setErrorDeCarga(false);
    } catch (err) {
      if (err.response?.status === 404 || err.response?.status === 403) {
        // ARREGLOS-PROFE (#378): un 403 con `group_institutional_only` es una cuenta que NO es
        // de profesor entrando por el link: la pantalla de salida dice eso (con el par
        // de `common:errors`) y no «no se pudo procesar este documento».
        setNoExiste(err.response?.data?.error_code === 'group_institutional_only' ? 'acceso' : true);
        return;
      }
      // Una lectura fallida NO borra lo que ya está en pantalla: se conserva la
      // última foto buena y el siguiente sondeo lo intenta otra vez. Pero si NO
      // hay foto —la primera carga falló— la pantalla no puede quedarse diciendo
      // «…» para siempre: ése es exactamente el esqueleto eterno que PILOTO-1C
      // cerró en las otras dos pantallas de profesor.
      setDatos(prev => {
        if (prev === null) setErrorDeCarga(true);
        return prev;
      });
    }
  }, [groupId, docId, decidirTemasAlLlegar]);

  // EL DOCUMENTO PUEDE CAMBIAR SIN QUE LA PANTALLA SE DESMONTE: react-router
  // reusa el componente cuando sólo cambian los params de la misma ruta. Sin este
  // barrido, las casillas palomeadas y el aviso viajarían del documento anterior
  // al nuevo. LA FOTO PREVIA SE BORRA TAMBIÉN: si no, el primer sondeo del
  // documento nuevo compararía contra los temas del anterior y sacaría banners
  // de un archivo que el profesor ya no está mirando.
  useEffect(() => {
    const enCache = readScreenCache(claveDeCache(groupId, docId))?.data || null;
    setDatos(enCache);
    // mezcla: otro documento en la misma pantalla nace plegado hasta que lleguen SUS temas.
    if (decididoPara.current !== claveDeCache(groupId, docId)) {
      setTemasAbiertos(false);
      decidirTemasAlLlegar(claveDeCache(groupId, docId), enCache?.temas);
    }
    setNoExiste(false);
    setErrorDeCarga(false);
    setMarcados({});
    // t7-1b (T6): el nombre y la fecha de publicar viven DENTRO de `modalPublicar`,
    // asi que esta linea tambien los limpia; la ventana chica y las cards editadas
    // son de ESTE documento y se van con el.
    setModalPublicar(null);
    setVentanaDeCard(null);
    cardsEditadas.current = {};
    setEnFilaOptimista({});
    fotoPrevia.current = null;
  }, [groupId, docId, decidirTemasAlLlegar]);

  useEffect(() => { cargar(); }, [cargar]);

  /* ── INFRA-2 (#364): «EN FILA» EN CUANTO LLEGA EL 202 ─────────────────────
   *
   * EL HUECO QUE ESTO CIERRA. `generar` hace dos idas y vueltas: el POST que
   * encola y el GET que relee. Entre las dos —cientos de milisegundos, o un
   * segundo largo con red mala— los temas seguían diciendo «Sin generar» con el
   * botón apagado: el profesor tocaba y no pasaba nada visible. La palabra de
   * Carlos («debió ser fluido y suave») describe exactamente ese hueco.
   *
   * SE PINTA LO QUE EL SERVIDOR DIJO QUE ENCOLÓ (`res.data.temas`) y no lo que
   * el profesor marcó: un encolado PARCIAL —el fusible diario cortando a la
   * mitad— dejaría «En fila» temas que nadie va a mover. Es la misma regla con
   * la que `anunciar` decide a quién apuntar en el vigilante global.
   *
   * DURA LO QUE TARDA LA RELECTURA: `cargar` lo limpia cuando llega la foto, y
   * de ahí en adelante manda el servidor. No puede quedarse pegado porque los
   * trabajos se crean ANTES del 202 (`encolar_temas` corre dentro de
   * `_disparar`), así que la primera foto ya los trae `en_fila` de verdad. */
  const [enFilaOptimista, setEnFilaOptimista] = useState({});   // {tema_id: true}

  const temasPintados = React.useMemo(() => {
    const base = datos?.temas || [];
    if (Object.keys(enFilaOptimista).length === 0) return base;
    return base.map(tm => (
      enFilaOptimista[tm.tema_id] && llevaCasilla(tm)
        ? { ...tm, estado: 'en_fila' }
        : tm));
  }, [datos, enFilaOptimista]);

  // EL SONDEO MIRA LOS TEMAS, y la diferencia muerde de verdad: «hay algo
  // moviéndose» es una propiedad de los TEMAS, no de un paquete. Atarlo al
  // paquete congelaba la pantalla en cuanto cerraba el más reciente, con los
  // temas de un paquete anterior todavía corriendo.
  //
  // INFRA-2 (#364): mira los temas PINTADOS y no los del servidor, así que el
  // intervalo arranca con el «En fila» optimista en vez de esperar a la foto.
  // Sin esto, el chip nuevo sería un adorno sobre una pantalla que no se vuelve
  // a preguntar nada hasta la relectura. La cadencia NO se toca: los 4 s de
  // `POLL_MS` ya estaban por debajo del «≤ 5 s» de la firma.
  const hayMovimiento = datos?.documento === 'leyendo'
    || temasPintados.some(t => t.estado === 'en_fila' || t.estado === 'generando');

  useEffect(() => {
    if (!hayMovimiento) return undefined;
    const id = setInterval(cargar, POLL_MS);
    return () => clearInterval(id);
  }, [hayMovimiento, cargar]);

  // INFRA-2 (#364): LA LISTA QUE PINTA LA PANTALLA ES LA OPTIMISTA. Es idéntica
  // a `datos.temas` salvo en el instante entre el 202 y la relectura.
  const temas = temasPintados;
  /* T6 — DE DÓNDE SALIÓ EL MATERIAL, y con ello qué rango se pinta. El servidor
     lo manda siempre (`pdf` para todo lo anterior a esta tanda), así que el `||`
     sólo cubre la primera pintada desde caché de una pantalla guardada antes. */
  const fuente = datos?.fuente || 'pdf';
  // UN DOCUMENTO DEL KIT DE ESTADOS ES PARA MIRARLO, NO PARA GENERARLO.
  //
  // El kit siembra documentos plenamente vivos —perfil institucional, `ready`,
  // temas elegibles— para que Carlos vea cada copy en producción. Si sus casillas
  // estuvieran vivas, un toque encolaría una corrida REAL sobre un documento que
  // no tiene ni manifiesto ni archivo: una llamada facturada a terra para no
  // producir nada. El servidor ya la niega (409 `documento_demo`); aquí se apagan
  // los botones para que ni siquiera se pueda intentar.
  const demo = !!datos?.demo;
  // LO QUE SE MANDA ES EXACTAMENTE LO QUE SE VE MARCADO, Y ES LA MISMA COMPUERTA.
  //
  // EL AGUJERO QUE ESTO CIERRA, y era de los caros: T4.5 dejo de pintar casilla
  // en «Listo para revisar», pero la seleccion seguia filtrandose con
  // `sePuedeMarcar`, que solo excluye los dos estados OCUPADOS. Un tema palomeado
  // cuando estaba «Sin generar» y que termina mientras el profesor mira perdia su
  // casilla de la pantalla y CONSERVABA su marca: el siguiente toque al boton
  // encolaba una corrida REAL y facturada sobre un tema que ya tenia preguntas y
  // que en pantalla no mostraba nada marcado.
  //
  // `llevaCasilla` es la unica fuente: si no hay casilla, no hay marca que valga.
  // Y como un tema PUBLICADO nunca lleva casilla, el candado de #123 queda
  // cubierto por la misma compuerta, sin una rama nueva.
  const seleccionables = (tema) => llevaCasilla(tema) && sePuedeMarcar(tema);
  const seleccionados = temas
    .filter(t => marcados[t.tema_id] && seleccionables(t))
    .map(t => t.tema_id);

  // ── LOS TEMAS QUE SE PUEDEN PUBLICAR HOY (T5.2, firma #133) ────────────────
  //
  // LA REGLA NUEVA, Y ES UNA SOLA: al menos una pregunta VIVA. Se acabó el
  // requisito de «revisado» — revisar es una herramienta de control del
  // profesor, no una puerta que haya que cruzar para darle material a sus
  // alumnos. Y un tema YA PUBLICADO también entra: es el REMIX, y basta él solo
  // para prender el botón.
  //
  // ES LA MISMA REGLA QUE VALIDA EL SERVIDOR por su cuenta
  // (`curacion.se_puede_publicar`): aquí decide si el botón se enciende, allá
  // decide si la operación procede, y el cliente JAMÁS es la única guarda.
  //
  // EL ORDEN ES EL DEL DOCUMENTO DENTRO DE CADA BLOQUE, y el modal los pinta en
  // dos bloques: primero lo que no ha salido —que es lo que el profesor viene a
  // mandar— y al final lo ya publicado. Se calcula aquí, en un solo sitio, para
  // que la lista del modal y el total no puedan discrepar.
  // UN TEMA QUE SE ESTÁ GENERANDO NO ENTRA, y esta línea cierra una puerta que
  // la regla nueva abrió sin querer. Hasta T5.1 la protección era un efecto
  // lateral: el gate pedía `estado === 'revisado'`, y un tema en vuelo nunca lo
  // es (el estado de la corrida gana al de la cola). Con «tiene material vivo»
  // esa protección desapareció — y un tema a media generación no sólo entraba:
  // entraba PRE-MARCADO. Publicar lo SELLARÍA a media corrida, y las preguntas
  // que cayeran después nacerían pendientes sobre un tema cerrado, o sea
  // incurables. El servidor lo niega por su cuenta con 409 `tema_en_curso`.
  const enVuelo = (t) => t.estado === 'en_fila' || t.estado === 'generando';
  const publicados = temas.filter(t => t.estado === 'publicado' && vivas(t) > 0);
  const sinPublicar = temas.filter(
    t => t.estado !== 'publicado' && !enVuelo(t) && vivas(t) > 0);
  const publicables = [...sinPublicar, ...publicados];

  /* ── SELECCIONAR TEXTO NO ES TOCAR LA CARD, SEGUNDA VERSIÓN ────────────────
   *
   * LA GARANTÍA NO CAMBIA (T5.2, #135 §8.5): en escritorio, soltar el ratón tras
   * arrastrar sobre el nombre de un tema para copiarlo despacha un `click` en el
   * ancestro común — o sea, en la card entera. Sin guarda, copiar el nombre de
   * un tema lo PALOMEA, y en esta pantalla palomear es lo que decide qué se va a
   * generar en el siguiente toque. No factura por sí solo, pero una marca
   * fantasma es lo que hace que ese toque no coincida con lo que el profesor
   * cree que pidió.
   *
   * LO QUE CAMBIA ES CÓMO SE RECONOCE EL ARRASTRE, Y ES EL DEFECTO 1.2 DEL
   * SMOKE. La primera versión preguntaba `window.getSelection()`, y esa pregunta
   * NO distingue las dos cosas que tiene que distinguir:
   *
   *   · el SEGUNDO click en el mismo punto es, para el navegador, un `dblclick`;
   *   · y un dblclick SELECCIONA LA PALABRA bajo el cursor, en el `mousedown`;
   *   · así que para cuando el `click` llegaba, `getSelection()` ya devolvía esa
   *     palabra y la guarda se comía el toque entero.
   *
   * Resultado, en el documento real de Carlos: la card se marcaba, y para
   * DESMARCARLA había que sacar el mouse, volver a entrar y hacer click — que es
   * lo que rompe la secuencia de dblclick y deja nacer un click limpio. El
   * remedio que él encontró solo describe la causa exacta.
   *
   * LO QUE SÍ SEPARA UN ARRASTRE DE UN TOQUE ES EL MOVIMIENTO DEL PUNTERO: en un
   * dblclick no se mueve entre `mousedown` y `click`; en una selección de texto,
   * sí, y bastante. Así que se mide eso.
   *
   * SIN `mousedown` REGISTRADO NO HAY ARRASTRE POSIBLE, y esa rama es la que
   * mantiene vivo el camino accesible: el `Espacio` del teclado dispara `change`
   * sin pasar por el ratón. */
  const UMBRAL_DE_ARRASTRE = 5;   // px. Un temblor de trackpad no llega.
  const arranque = useRef(null);

  /** ¿El `click` que estamos atendiendo es el final de un arrastre?
   *
   *  CONSUME el punto de arranque, así que dos preguntas seguidas sobre el mismo
   *  gesto no pueden contestar cosas distintas — y un `click` sintético que
   *  llegue después no hereda el `mousedown` de otro. */
  const huboArrastre = (ev) => {
    const inicio = arranque.current;
    arranque.current = null;
    if (!inicio || !ev || ev.clientX === undefined) return false;
    return Math.hypot(ev.clientX - inicio.x, ev.clientY - inicio.y)
      > UMBRAL_DE_ARRASTRE;
  };

  const alternar = (tema, ev) => {
    if (demo || !seleccionables(tema)) return;
    if (huboArrastre(ev)) return;
    setMarcados(prev => ({ ...prev, [tema.tema_id]: !prev[tema.tema_id] }));
  };

  const generar = async () => {
    if (seleccionados.length === 0) {
      // TANDA 26b (#237) — AL TOAST, COMO TODO LO DEMÁS DE ESTA PANTALLA.
      sacarBanners([{ clave: CLAVE_SIN_SELECCION, tono: 'aviso',
        texto: t('groups.bank.temas.no_selection') }]);
      return;
    }
    setEnviando(true);
    try {
      const res = await api.post(`/groups/${groupId}/bank/${docId}/generar`,
        { temas: seleccionados });
      anunciar(res);
      setMarcados({});
      await cargar();
    } catch (err) {
      // 409 `tema_en_curso` significa que la foto estaba vieja: se relee y el
      // profesor ve el estado real en vez de un mensaje que no puede accionar.
      // El 503 del fusible diario SÍ trae un texto que hay que leer.
      mostrarFallo(err);
      await cargar();
    } finally { setEnviando(false); }
  };

  // EL TOAST SÓLO SI DE VERDAD EMPEZÓ ALGO. El servidor puede encolar MENOS de lo
  // pedido —el fusible diario de la plataforma para en seco— y decir «Empezamos a
  // generar» sobre una lista que no se va a mover es justo la clase de mentira
  // que esta pantalla existe para quitar. (Con CERO encolados el servidor ya no
  // contesta 202: manda un 503 con su texto, y cae en `mostrarFallo`.)
  const anunciar = (res) => {
    /* INFRA-2 (#364): EL PINTADO OPTIMISTA VA ANTES QUE EL TOAST y antes de la
       relectura. Se hace aquí y no en `generar` para que «Generar más de este
       tema» lo herede sin duplicar la regla: los dos llaman al mismo
       `_disparar` y los dos reciben la misma lista de encolados. */
    const encolados = res?.data?.temas || [];
    if (encolados.length) {
      setEnFilaOptimista(prev => ({
        ...prev, ...Object.fromEntries(encolados.map(id => [id, true])),
      }));
    }
    if ((res?.data?.encolados || 0) === 0) return;
    toast.success(t('groups.bank.temas.started_toast'));
    // TANDA 26b (#236) — Y SE APUNTAN EN EL VIGILANTE GLOBAL.
    //
    // El copy que acaba de salir dice «Puedes salir». Hasta aquí, salir costaba
    // el aviso del final: los banners de tema los produce el diff de esta
    // pantalla contra `fotoPrevia`, que es una ref y muere al desmontar. El
    // vigilante vive por encima de las rutas y sigue el mismo tema hasta su
    // estado final, con las MISMAS claves — si por milisegundos los dos
    // disparan, sonner deduplica.
    //
    // SE APUNTA LO QUE EL SERVIDOR DICE QUE ENCOLÓ (`res.data.temas`) y no lo
    // que el profesor marcó: un disparo parcial —el fusible diario cortando a la
    // mitad— dejaría apuntados temas que nadie va a mover.
    for (const temaId of (res?.data?.temas || [])) {
      registrarTemaEncolado({
        groupId, docId, temaId,
        nombre: (temas.find(tm => tm.tema_id === temaId) || {}).nombre || '',
      });
    }
  };

  // El `detail` del servidor se pinta TAL CUAL cuando es texto: los copys de esta
  // superficie viven en el archivo del servidor, en español, igual que los de la
  // subida. Si no dijo nada legible, no se inventa nada — la relectura de abajo
  // enseña el estado real.
  /** Saca el aviso del servidor cuando lo hay. **Devuelve si dijo algo**, que
   *  es lo que le permite a `publicar` decidir si cierra su modal.
   *
   *  TANDA 26b (#237) — VA EN TOAST Y NO EN EL PIE. Es el mismo patrón con el
   *  que la pantalla de curación ya saca el 429 del cupo (firma #124): el copy
   *  del servidor, tal cual, por la cola de la casa. El recuadro del pie murió
   *  con esta tanda — era el último sitio del carril donde un aviso salía sin
   *  moverse y sin caducar, justo debajo del pliegue en una lista larga. */
  const mostrarFallo = (err) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      sacarBanners([{ clave: CLAVE_FALLO_GENERAR, tono: 'aviso', texto: detail }]);
      return true;
    }
    /* ── INFRA-2 (#365): EL SOBRE-OBJETO DE LAS CINCO PUERTAS ────────────────
     *
     * `_disparar` levanta sus cinco 4xx con un `detail` que es un OBJETO
     * (`{"error": "tema_en_curso"}`) y sin `error_code` hermano, así que el
     * interceptor de `lib/api.js` no encuentra `common:errors.<código>`, deja el
     * objeto INTACTO y sin `message`, y esta función —que sólo sabía leer texto—
     * se iba en silencio. Resultado: el profesor tocaba «Generar», no pasaba
     * nada, y no había forma de saber por qué. Los cinco eran mudos en los dos
     * lados (el servidor tampoco dejaba rastro; eso se arregló allá).
     *
     * SE TRADUCE AQUÍ Y NO EN `common:errors` A PROPÓSITO: allí los recogería el
     * interceptor y estos códigos entrarían al censo de `error_code`
     * (`api.errorCode.tanda8`), que esta tanda deja clavado en 167. Son avisos
     * de UNA superficie, y viven con el resto del copy de esa superficie. */
    const codigo = detail && typeof detail === 'object' ? detail.error : null;
    const llave = codigo ? AVISO_POR_CODIGO[codigo] : undefined;
    if (llave) {
      sacarBanners([{ clave: CLAVE_FALLO_GENERAR, tono: 'aviso', texto: t(llave) }]);
      return true;
    }
    return false;
  };

  const generarMas = async (temaId) => {
    setTrabajando(p => ({ ...p, [temaId]: true }));
    try {
      const res = await api.post(
        `/groups/${groupId}/bank/${docId}/temas/${temaId}/generar-mas`);
      anunciar(res);
      await cargar();
    } catch (err) {
      mostrarFallo(err);
      await cargar();
    } finally { setTrabajando(p => ({ ...p, [temaId]: false })); }
  };

  const abrirPublicar = (e) => {
    volverDePublicar.current = quienAbrio(e);
    // LO NO PUBLICADO, PRE-MARCADO; LO YA PUBLICADO, NO (T5.2, firma #133).
    //
    // El caso normal es mandar todo lo que aún no ha salido, así que desmarcar
    // es la excepción y la excepción cuesta un toque. Con lo ya publicado la
    // norma es la contraria: republicarlo es una decisión deliberada —el
    // profesor está armando una card nueva con material que sus alumnos YA
    // tienen— y una casilla premarcada lo convertiría en un accidente.
    setModalPublicar({
      marcados: Object.fromEntries(sinPublicar.map(t => [t.tema_id, true])),
      // QUIÉN YA HABÍA SALIDO CUANDO ESTE MODAL SE ABRIÓ. Ver `alternarEnModal`:
      // es lo que distingue un remix DELIBERADO de uno que el sondeo fabricó.
      yaHabianSalido: new Set(publicados.map(t => t.tema_id)),
      // EL ORDEN DE LAS FILAS SE CONGELA AL ABRIR, y los DATOS de cada una
      // siguen vivos. Es la mitad que hay que congelar y la mitad que no:
      //
      //   · Congelar el ORDEN, porque el sondeo corre cada 4 s mientras algo
      //     genera y una pregunta que aterriza vuelve publicable a su tema —
      //     insertándole una fila de 44 px EN MEDIO de la lista, en el orden del
      //     documento. El dedo cae donde estaba una fila y marca otra.
      //   · NO congelar los CONTEOS, porque el servidor publicará lo que haya en
      //     el momento del POST: enseñar un total viejo sería prometer una card
      //     distinta de la que va a nacer.
      orden: publicables.map(t => t.tema_id),
      // t7-1b (T6): EL NOMBRE Y LA FECHA DE LA CARD NACEN VACIOS en cada apertura.
      // Viven aqui dentro y no en un estado aparte: cerrar la ventana (`null`) y
      // cambiar de documento ya los limpian, sin una linea mas.
      nombre: '',
      caduca: '',
    });
  };

  /** Marcar/desmarcar dentro del modal. En un solo sitio porque lo tocan dos
   *  cosas —el renglón entero y su casilla— y dos copias de un `set` se
   *  desvían. */
  const alternarEnModal = (temaId) => setModalPublicar(m => ({
    ...m,
    marcados: { ...m.marcados, [temaId]: !m.marcados[temaId] },
    // MARCARLO A MANO LO VUELVE DELIBERADO, aunque haya llegado por el sondeo.
    yaHabianSalido: new Set([...(m.yaHabianSalido || []), temaId]),
  }));

  /** ¿ESTE TEMA VA MARCADO, DE VERDAD?
   *
   *  EL CASO QUE ESTO CIERRA, y es fino: el modal siembra sus marcas al abrirse,
   *  pero la lista se recalcula con cada tic del sondeo. Si OTRA sesión publica
   *  un tema que aquí estaba premarcado, el sondeo lo mueve al bloque «Ya
   *  publicado»… **con su casilla todavía marcada**, y confirmar dispararía un
   *  REMIX que el profesor nunca decidió — justo la decisión deliberada que la
   *  casilla desmarcada de los publicados existe para exigir.
   *
   *  La marca de un tema que CRUZÓ la frontera mientras el modal estaba abierto
   *  se suelta; la de uno que ya estaba publicado al abrir, o que el profesor
   *  palomeó a mano, se respeta. */
  const marcadoEnModal = (t) => {
    if (!modalPublicar?.marcados?.[t.tema_id]) return false;
    if (t.estado !== 'publicado') return true;
    return !!modalPublicar.yaHabianSalido?.has(t.tema_id);
  };

  const publicar = async () => {
    // SE MANDA LO QUE EL PROFESOR VE MARCADO, o sea lo de la lista congelada.
    const elegidos = (modalPublicar?.orden || [])
      .map(id => publicables.find(t => t.tema_id === id))
      .filter(t => t && marcadoEnModal(t))
      .map(t => t.tema_id);
    if (!elegidos.length) return;
    // t7-1b (T6): EL NOMBRE Y LA FECHA VIAJAN SOLO CUANDO TIENEN VALOR. El nombre va
    // recortado (vacio = sin llave); la fecha es el INICIO del dia elegido en la zona del
    // navegador, que el servidor usa para decidir que es "hoy".
    // t7-2a remate (R5.2): la zona del navegador viaja SIEMPRE (antes solo con la fecha):
    // el servidor la guarda en la card y el correo del domingo sale a las 8:00 de esa zona.
    const cuerpo = { temas: elegidos };
    const nombre = (modalPublicar?.nombre || '').trim();
    if (nombre) cuerpo.nombre = nombre;
    const caduca = inicioDelDiaLocalISO(modalPublicar?.caduca);
    if (caduca) cuerpo.caduca = caduca;
    cuerpo.tz = zonaDelNavegador();
    setPublicando(true);
    try {
      const res = await api.post(`/groups/${groupId}/bank/${docId}/publicar`, cuerpo);
      setModalPublicar(null);
      sacarBanners([{ clave: `pub:${res.data?.numero}`, tono: 'exito',
        texto: bannerPublicacionCreada(t, res.data?.numero) }]);
      await cargar();
    } catch (err) {
      // EL MODAL SE CIERRA SÓLO SI HAY ALGO QUE DECIR. `mostrarFallo` pinta el
      // `detail` del servidor cuando es texto; ante un fallo de red no hay
      // `response` ni `detail`, así que cerrar el modal dejaba la pantalla
      // exactamente igual que antes de tocar el botón: sin card, sin aviso, y
      // con TODAS las marcas perdidas — incluidas las de un remix, que son
      // manuales una por una. Dejarlo abierto es lo honesto: no pasó nada, y el
      // profesor puede reintentar sin rehacer su trabajo.
      // t7-1b (T6): `caduca_invalida` se porta COMO LOS DEMAS: dice algo, asi que
      // cierra (y el nombre y la fecha se van con la ventana).
      const dijoAlgo = mostrarFallo(err);
      if (dijoAlgo) setModalPublicar(null);
      // t7-3 remate 7 (pasada 4, regla 7): sin mensaje del servidor, la ventana se queda (para reintentar) pero avisa
      else toast.error(t(err?.response ? 'common:toasts.generic_error' : 'common:errors.network'));
      await cargar();
    } finally { setPublicando(false); }
  };

  /* ── t7-1b (T6): CAMBIAR EL NOMBRE O LA FECHA DE UNA CARD YA PUBLICADA ───────
   *
   * UNA VENTANA CHICA POR ACCION, las dos con la misma pieza que la de publicar.
   * "Cambiar nombre" arranca con el nombre PROPIO de la card (vacio si la card se
   * llama por sus temas); "Cambiar fecha", con su fecha si la tiene.
   *
   * EL RENGLON SE ACTUALIZA SIN RELEER: la respuesta del PATCH ES la card como
   * quedo, con la misma forma que la lista, y reemplaza a la vieja en memoria y en
   * la foto guardada. `cardsEditadas` impide que un tic del sondeo pedido antes la
   * deshaga (ver `conCardsEditadas`).
   *
   * EL FALLO: `mostrarFallo` lo pinta como los demas. La ventana SE QUEDA con
   * `caduca_invalida`, que es justo el caso en que el profesor tiene que corregir
   * la fecha; con otro aviso (la card ya no existe) se cierra, y sin aviso (un
   * fallo de red) se queda para reintentar, como la de publicar. Despues de
   * cualquier fallo se relee: la foto real manda. */
  const abrirCambiarNombre = (card, e) => {
    volverDeCard.current = quienAbrio(e);
    setVentanaDeCard({ tipo: 'nombre', card, valor: card.nombre_propio ? (card.nombre || '') : '' });
  };
  const abrirCambiarFecha = (card, e) => {
    volverDeCard.current = quienAbrio(e);
    setVentanaDeCard({
    // t7-3 remate 5 (A3, #572): abre con el dia que eligio el profe, no con el de la zona de este navegador
    tipo: 'fecha', card, valor: valorDelCalendario(card),
    });
  };
  const escribirEnVentana = (valor) =>
    setVentanaDeCard(v => (v ? { ...v, valor } : v));

  const editarCard = async (cambio) => {
    const card = ventanaDeCard?.card;
    if (!card) return;
    setGuardandoCard(true);
    try {
      const res = await api.patch(
        `/groups/${groupId}/bank/${docId}/publicaciones/${card.id}`, cambio);
      const nueva = res?.data;
      setVentanaDeCard(null);
      if (nueva && nueva.id) {
        cardsEditadas.current = {
          ...cardsEditadas.current,
          [nueva.id]: { card: nueva, hasta: lecturas.current },
        };
        setDatos(prev => {
          if (!prev) return prev;
          const siguiente = { ...prev, publicaciones: (prev.publicaciones || [])
            .map(p => (p.id === nueva.id ? nueva : p)) };
          writeScreenCache(claveDeCache(groupId, docId), siguiente);
          return siguiente;
        });
      } else {
        await cargar();
      }
    } catch (err) {
      const dijoAlgo = mostrarFallo(err);
      const codigo = err?.response?.data?.detail?.error;
      if (dijoAlgo && codigo !== 'caduca_invalida') setVentanaDeCard(null);
      // t7-3 remate 7 (pasada 4, regla 7): sin mensaje del servidor, la ventana se queda (para reintentar) pero avisa
      if (!dijoAlgo) toast.error(t(err?.response ? 'common:toasts.generic_error' : 'common:errors.network'));
      await cargar();
    } finally { setGuardandoCard(false); }
  };

  const guardarCard = () => {
    if (!ventanaDeCard) return undefined;
    if (ventanaDeCard.tipo === 'nombre') {
      // Vacio (o solo espacios) = volver al nombre de los temas.
      return editarCard({ nombre: (ventanaDeCard.valor || '').trim() || null });
    }
    const caduca = inicioDelDiaLocalISO(ventanaDeCard.valor);
    if (!caduca) return undefined;
    return editarCard({ caduca, tz: zonaDelNavegador() });
  };
  const quitarFecha = () => editarCard({ caduca: null });

  const volver = () => navigate(`/groups/${groupId}`);
  const revisar = (temaId) =>
    navigate(`/groups/${groupId}/bank/${docId}/temas/${temaId}`);

  if (noExiste) {
    return (
      <div className="max-w-3xl mx-auto space-y-4" data-testid="temas-no-existe">
        <StickyBackPill onClick={volver} label={t('groups.bank.temas.back')}
          testid="temas-back-pill" buttonTestid="temas-back-btn" />
        <p className="text-sm text-[#a0a0a0]" data-testid="temas-no-existe-texto">
          {t(noExiste === 'acceso' ? 'common:errors.group_institutional_only' : 'groups.bank.temas.doc_error')}
        </p>
      </div>
    );
  }

  // EL TERCER ESTADO SE REUSA, NO SE COPIA. `GroupsLoadError` es el bloque que
  // PILOTO-1C creó para esta misma situación en las otras dos pantallas de
  // profesor, con su copy en un solo sitio y su botón de reintentar de 44 pt.
  // ES LA ÚNICA CADENA DE ESTA PANTALLA QUE SALE DEL ÁRBOL DE i18n, y es una
  // excepción deliberada: la alternativa era inventar un copy que la firma #118
  // no tiene, o dejar la pantalla girando.
  if (errorDeCarga && datos === null) {
    return (
      <div className="max-w-3xl mx-auto space-y-4" data-testid="temas-error-de-carga">
        <StickyBackPill onClick={volver} label={t('groups.bank.temas.back')}
          testid="temas-back-pill" buttonTestid="temas-back-btn" />
        <GroupsLoadError onRetry={cargar} testid="temas-load-error" />
      </div>
    );
  }

  const danio = datos?.danio || null;
  // t7-1b (T6): LAS CARDS DE ESTE DOCUMENTO, de la mas reciente a la mas vieja (el
  // orden lo manda el servidor). Siempre con `|| []`: una foto guardada antes de esta
  // tanda no trae la llave.
  const cards = datos?.publicaciones || [];
  // LAS FILAS DEL MODAL: el orden que se congeló al abrir, con los datos vivos
  // de cada tema. Un tema que desapareció entre medias (otro borró el documento)
  // sencillamente no está.
  const enElModal = (modalPublicar?.orden || [])
    .map(id => publicables.find(t => t.tema_id === id))
    .filter(Boolean);
  const marcadosEnModal = enElModal.filter(marcadoEnModal);
  const totalAPublicar = marcadosEnModal.reduce((s, t) => s + vivas(t), 0);
  // LA LÍNEA CONDICIONAL MIRA LO MARCADO, no el documento entero: avisar de
  // material sin revisar que el profesor acaba de desmarcar sería un aviso sobre
  // algo que no va a pasar.
  const hayPendientesMarcadas = marcadosEnModal.some(t => (t.pendientes || 0) > 0);

  /* ── LA BOTONERA, Y VIVE DOS VECES (firma #135) ────────────────────────────
     Los dos botones van JUNTOS, arriba y abajo de la lista. Abajo porque una
     lista de veinte temas deja los controles fuera de pantalla justo cuando el
     profesor acaba de terminar de marcar — que es el momento exacto en que los
     necesita. Se extrae a una función y no se copia el JSX: dos copias de una
     botonera se desvían en el primer retoque.

     ES UNA FUNCIÓN QUE SE LLAMA (`{acciones()}`), NO UN COMPONENTE QUE SE MONTA
     (`<Acciones />`), y la diferencia NO es de estilo. Un componente declarado
     dentro del cuerpo de otro es una función NUEVA en cada render, así que React
     lo ve como un TIPO distinto cada vez: desmonta el subárbol entero y lo vuelve
     a crear. Esta pantalla re-renderiza cada 4 s por su sondeo — los dos botones
     se destruirían y recrearían en cada tic, perdiendo el foco del teclado en
     medio de un toque. Llamándola, su JSX se funde con el del padre y React
     reconcilia los nodos como lo que son: los mismos. */
  const acciones = (posicion) => (
    /* LOS MISMOS DOS BOTONES, DOS VECES (firma #135), así que sus `data-testid`
       salen DUPLICADOS en el DOM a propósito. `data-posicion` los desempata sin
       inventar dos nombres para el mismo botón: una prueba que necesite UNO
       concreto pide `[data-testid="temas-acciones"][data-posicion="abajo"]`. */
    <div className="flex flex-wrap items-center gap-2"
      data-testid="temas-acciones" data-posicion={posicion}>
      <Button onClick={generar} disabled={enviando}
        className="bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl
          min-h-[44px] text-sm disabled:opacity-40"
        data-testid="temas-generar-btn">
        {enviando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
        {t('groups.bank.temas.generate_btn')}
      </Button>
      {/* PUBLICAR: SIEMPRE PRESENTE, APAGADO CON SU PORQUÉ (T5.2, firma #133).
          Hasta T5.1 no se pintaba cuando no había nada publicable, y eso le
          escondía la función entera al profesor que más la necesitaba: el que
          todavía no tiene preguntas. Apagado enseña que existe; el renglón de al
          lado enseña qué falta. */}
      <Button onClick={abrirPublicar} disabled={publicables.length === 0}
        className="bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl
          min-h-[44px] text-sm disabled:opacity-40"
        data-testid="temas-publicar-btn">
        {t('groups.bank.publicar.btn')}
      </Button>
      {publicables.length === 0 && (
        <span className={META} data-testid="temas-publicar-porque">
          {t('groups.bank.publicar.nothing_to_publish')}
        </span>
      )}
    </div>
  );

  /* series-7 (#484) -- EL TITULO DE UNA SECCION ES EL RENGLON QUE LA PLIEGA.
     Molde de `InvitaAmigo.js`: el boton va DENTRO del `h2` (el patron de acordeon de
     ARIA; un `button` no admite un `h2` dentro), asi que el encabezado conserva su
     clase, su marca y su texto. Sin texto nuevo: el nombre accesible del boton ES el
     titulo, y la flecha se esconde del lector (el estado lo anuncia
     `aria-expanded`). Es una funcion que se LLAMA y no un componente, por lo mismo
     que `acciones`: el sondeo re-renderiza cada 4 s.

     PLEGADA NO ES DESMONTADA. El cuerpo se queda en el DOM con el atributo de HTML
     que lo oculta (el prop de React, no una clase): lo marcado no se pierde al
     plegar, y las pruebas de la pantalla siguen leyendo renglones y botones sin
     abrir nada. Ese atributo solo va en nodos SIN utilidad de display, porque una
     utilidad de display le gana a la regla de la hoja base que lo hace valer; por
     eso las dos botoneras, que traen la suya, van dentro de un envoltorio propio.
     Los botones Generar y Publicar viven en la seccion de temas: se pliegan con
     ella. */
  /* remates-1-3-0c (Q6, W10): EL RENGLON ES LA CABECERA DE LA SECCION, de 56 px: la guia naranja
     de la marca, el titulo, el chip gris con el conteo real y la flecha. Toda la cabecera abre y
     cierra (es el mismo boton). El chip lleva su marca (`<testid-del-titulo>-conteo`). Decia: un
     renglon de 44 px con el titulo y la flecha. */
  const renglonPlegable = (titulo, abierto, alternar, testid, conteo, testidConteo) => (
    <button type="button" onClick={() => alternar(v => !v)} aria-expanded={abierto}
      className={CABECERA_DE_SECCION}
      data-testid={testid}>
      <span className={GUIA_DE_SECCION} aria-hidden="true" />
      <span className="flex-1 min-w-0 truncate" data-testid={`${testid}-titulo`}>{titulo}</span>
      <span className={CHIP_DE_CONTEO} data-testid={testidConteo}>{conteo}</span>
      <ChevronDown
        className={`w-4 h-4 text-[#8c8c8c] shrink-0 transition-transform ${abierto ? 'rotate-180' : ''}`}
        aria-hidden="true"
      />
    </button>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="temas-page">
      <StickyBackPill onClick={volver} label={t('groups.bank.temas.back')}
        testid="temas-back-pill" buttonTestid="temas-back-btn" />

      {/* EL TÍTULO DE LA PANTALLA, A SU TAMAÑO DE VERDAD (T5.2, §4.2). Estaba en
          `text-lg font-semibold`: el MISMO peso y tamaño que «Temas del
          documento», su propio subtítulo de sección. Con los dos iguales, entrar
          a un documento no se sentía como haber llegado a ninguna parte. */}
      <h1 className={`${TITULO_PANTALLA} flex items-center gap-2 min-w-0`}
        data-testid="temas-filename">
        {/* t7-2b (E3): el glifo del tipo de material (hoja, video o audio) segun la `fuente`
            que ya llega, al tamano y color de la hoja que habia y sin caja. */}
        <GlifoDeMaterial tipo={tipoDeMaterial({ file_type: fuente })}
          className="w-5 h-5 text-[#FF6B00] shrink-0" />
        {/* t7-3 remate 8 (#591, C44 y C115): el nombre del documento parte renglon (antes se recortaba) */}
        <span className="min-w-0 break-words">{datos?.filename || ''}</span>
      </h1>

      {datos === null ? (
        /* El esqueleto se viste igual que lo que va a sustituir: sin la card
           gigante, o la lista brincaría de un marco a ninguno al llegar la foto. */
        <div className="space-y-3" data-testid="temas-cargando">
          <div className="h-4 w-40 rounded animate-pulse bg-white/[0.06]" />
          <EsqueletoDeTemas />
        </div>
      ) : datos.documento === 'leyendo' ? (
        <p className="text-sm text-[#a0a0a0]" data-testid="temas-doc-leyendo">
          {t('groups.bank.temas.doc_reading')}
        </p>
      ) : datos.documento === 'error' ? (
        <p className="text-sm text-red-300" data-testid="temas-doc-error">
          {t('groups.bank.temas.doc_error')}
        </p>
      ) : (
        <>
          {/* EL BANNER FUERTE (copy 13), INTACTO: mitad o más de páginas
              ilegibles. No bloquea nada. */}
          {datos.pdf_danado && (
            <div className="rounded-xl border border-amber-400/40 bg-amber-400/10 p-3
              flex items-start gap-2" data-testid="temas-pdf-danado">
              <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-200">{t('groups.bank.temas.pdf_damaged')}</p>
            </div>
          )}

          {/* EL BANNER SUAVE (copy N1) — LO QUE ANTES ERA SILENCIO. Por debajo del
              umbral la pantalla no decía nada, y ese silencio le escondía al
              profesor la explicación de por qué un tema salió pobre. Los dos
              banners son excluyentes: el nivel decide cuál, nunca los dos. */}
          {danio && danio.nivel === 'leve' && (
            <div className="rounded-xl border border-amber-400/25 bg-amber-400/[0.06]
              p-3" data-testid="temas-danio-leve">
              <p className="text-sm text-amber-200/90">
                {danioParcial(t, danio.ilegibles, danio.total)}
              </p>
            </div>
          )}

          {temas.length === 0 ? (
            <p className="text-sm text-[#a0a0a0]" data-testid="temas-sin-temas">
              {t('groups.bank.temas.no_topics')}
            </p>
          ) : (
            /* ── EL VESTIDO DE T5.1 (firma #132, §4.3) ──────────────────────
               MUERE LA CARD GIGANTE. Envolvía la lista entera, así que la
               pantalla era un marco con marcos dentro y el borde de fuera
               competía con el de cada tema, que es el que de verdad importa.
               Ahora el título y la instrucción van como texto sobre el fondo y
               cada tema queda suelto, como su propia tarjeta.

               EL `data-testid` SE CONSERVA: lo miran siete pruebas de T4 y T4.5
               que no tienen por qué enterarse de un cambio de ropa. Lo que se va
               es `glass-card` —con su `transition: all .3s`, la enfermedad
               medida en flashcards, COS-18 y COS-32— y su relleno. */
            /* ajustes-1-3-0 (Bloque F3, ajuste 5): la seccion plegable vive dentro de un
               recuadro con la capa de las tarjetas de esta pantalla; el renglon del titulo
               con su flecha es la cabecera del recuadro. Sigue plegada al entrar (#484).
               remates-1-3-0c (Q6, W10): la seccion es una capa MAS OSCURA que las tarjetas
               (CAPA_DE_SECCION), con su cabecera de 56 px y, abierta, el cuerpo con su linea y
               su relleno (CUERPO_DE_SECCION); plegada, solo la cabecera. El cuerpo entero es el
               nodo que esconde (sin utilidad de display, #484). */
            <div className={CAPA_DE_SECCION} data-testid="temas-seccion">

              {/* ── EL ENCABEZADO COMPACTO (T5.2, §4.2) ────────────────────
                  Título de sección → los dos botones juntos → las dos líneas de
                  instrucción → la lista. El orden importa: el profesor lee qué
                  es esto, ve qué puede hacer, y sólo entonces le explicamos cómo.
                  El hueco muerto que había entre encabezado y lista se cierra
                  con el ritmo del sistema, no con un margen a ojo. */}
              <h2 className={TITULO_DE_SECCION_PLEGABLE} data-testid="temas-encabezado">
                {renglonPlegable(t('groups.bank.temas.heading'), temasAbiertos,
                  setTemasAbiertos, 'temas-seccion-plegar',
                  t('doc_card.topic_count', { count: temas.length }), 'temas-seccion-conteo')}
              </h2>
              <div hidden={!temasAbiertos} className={CUERPO_DE_SECCION} data-testid="temas-seccion-cuerpo">
              <div>{acciones('arriba')}</div>

              <div className="space-y-1">
                <p className="text-xs text-[#9a9a9a]" data-testid="temas-instruccion">
                  {t('groups.bank.temas.instruction')}
                </p>
                {/* F2 (#134) — LA LÍNEA QUE ESTRENA #133. Sin ella, el botón
                    `Publicar` recién estrenado no explica en ningún sitio que
                    revisar dejó de ser obligatorio, que es justo lo que el
                    profesor tiene que aprender de esta pantalla. */}
                <p className="text-xs text-[#9a9a9a]"
                  data-testid="temas-instruccion-publicar">
                  {t('groups.bank.temas.instruction_publish')}
                </p>
              </div>

              <div className={RITMO_LISTA} data-testid="temas-lista">
                {temas.map(tema => {
                  const marcable = seleccionables(tema) && !demo;
                  const ocupado = !!trabajando[tema.tema_id];
                  const casilla = llevaCasilla(tema);
                  const conMaterial = CON_MATERIAL.includes(tema.estado);
                  const publicado = tema.estado === 'publicado';
                  const fallido = tema.estado === 'no_se_pudo';
                  const sinMaterial = tema.estado === 'sin_material';
                  const danadas = tema.paginas_danadas || 0;
                  return (
                    /* ELEVACION SUTIL, Y SOLO DE COLOR Y SOMBRA. La transicion se
                       declara sobre `colors` y `box-shadow` a proposito: un
                       `transition: all` sobre una tarjeta cuya altura cambia cada
                       4 s por el sondeo re-acomoda la lista con animacion (la
                       enfermedad de `glass-card`, medida en flashcards, COS-18 y
                       COS-32). El `hoverable:` es la variante del repo: en
                       telefono un `:hover` se queda pegado. */
                    /* ── LA CARD ENTERA MARCA SU CASILLA (firma #135) ──────
                       El blanco táctil deja de ser una casilla de 16 px y pasa a
                       ser la tarjeta completa: en un teléfono, apuntarle a un
                       cuadradito es la diferencia entre marcar cinco temas de un
                       tirón y fallar dos de cada tres.

                       CÓMO SE EVITA EL DOBLE TOGGLE, que es el defecto clásico
                       de este patrón: el `<label>` que envolvía la fila MUERE. Un
                       label que envuelve a su input hace que un click en el texto
                       dispare TAMBIÉN un click sintético sobre el input, y con un
                       manejador en la tarjeta los dos burbujean: dos toggles, o
                       sea ninguno. Ahora la fila es un `<div>`, la casilla se
                       nombra con `aria-labelledby` (sigue siendo el control real
                       para teclado y lector) y su click se PARA en seco con
                       `stopPropagation` — su `onChange` ya alterna, y el `Espacio`
                       del teclado dispara `change` sin click, así que el camino
                       accesible queda intacto. */
                    <div key={tema.tema_id}
                      onMouseDown={marcable ? (e) => {
                        // DÓNDE EMPEZÓ EL GESTO. Ver `huboArrastre`: es lo único
                        // que separa un toque de una selección de texto, y lo que
                        // arregla el toggle que exigía sacar el mouse (T5.3).
                        arranque.current = { x: e.clientX, y: e.clientY };
                      } : undefined}
                      onClick={marcable ? (e) => alternar(tema, e) : undefined}
                      className={`${CAPA} ${RELLENO_CAPA} ${CAPA_TOCABLE}
                        ${marcable ? 'cursor-pointer' : ''}`}
                      data-testid={`tema-${tema.tema_id}`}>

                      {/* ── LÍNEA 1: casilla · título en una línea · chip ── */}
                      {/* 44 PT, COMO TODO LO DEMAS DEL CARRIL. Es el control que
                          decide que se genera —o sea, que se paga— y por un rato
                          fue el objeto tactil mas chico de la superficie del
                          profesor, con 28 px. El defecto original NO era el alto:
                          era `items-start`, que dejaba la casilla flotando sobre
                          veinte pixeles de aire muerto. */}
                      <div className="flex items-center gap-2.5 min-h-[44px]">
                        {casilla ? (
                          /* T6 / #282 — EL CUADRITO PASA A SER EL CÍRCULO.
                             El `<input>` NO desaparece: se vuelve invisible
                             (`sr-only`) y sigue siendo el control de verdad, con
                             su foco, su tecla Espacio y su semántica nativa de
                             casilla. Lo que se ve es `CirculoPalomita`, el mismo
                             que el selector de temas del alumno.
                             Va dentro de un `<label>` para que el círculo también
                             sea zona de toque del input — la card entera ya lo
                             era, esto sólo evita que el círculo sea el único
                             píxel muerto de la fila. */
                          <label className="shrink-0 inline-flex items-center cursor-pointer"
                            onClick={e => e.stopPropagation()}>
                            <input type="checkbox" className="sr-only peer"
                              checked={!!marcados[tema.tema_id]} disabled={!marcable}
                              onChange={() => alternar(tema)}
                              aria-labelledby={`tema-nombre-${tema.tema_id}`}
                              data-testid={`tema-check-${tema.tema_id}`} />
                            <CirculoPalomita marcado={!!marcados[tema.tema_id]}
                              className="peer-focus-visible:ring-2 peer-focus-visible:ring-[#FF6B00]" />
                          </label>
                        ) : (
                          // NO ES UNA CASILLA APAGADA: es la columna vacía que
                          // mantiene los títulos alineados entre cards. Sin ella
                          // la lista se lee dentada cada vez que un tema cambia
                          // de estado. Crece de 4 a 5 con el círculo, que es un
                          // pelín más ancho que el cuadrito que sustituye.
                          <span className="w-5 shrink-0" aria-hidden="true" />
                        )}
                        {/* t7-3 remate 8 (#591, C44 y C115): el nombre del tema parte renglon (antes se recortaba) */}
                        <span className="text-sm text-white flex-1 min-w-0 break-words"
                          id={`tema-nombre-${tema.tema_id}`}
                          title={renglonDeTema(t, tema, fuente)}
                          data-testid={`tema-nombre-${tema.tema_id}`}>
                          {tema.nombre}
                        </span>
                        <span className={`${CHIP_BASE} ${CHIPS[tema.estado] || ''}`}
                          data-testid={`tema-estado-${tema.tema_id}`}>
                          {tema.estado === 'en_fila' && (
                            <span className="w-1.5 h-1.5 rounded-full bg-[#8a8a8a]
                              shrink-0" aria-hidden="true" />
                          )}
                          {textoDelChip(t, tema)}
                        </span>
                      </div>

                      {/* ── LÍNEA 2: págs · conteo · etiqueta · botones ── */}
                      {/* ALTURAS UNIFORMES: la linea 2 reserva el alto del boton
                          (h-8) aunque el tema no lo lleve, para que una card
                          «Sin generar» y una «Listo para revisar» midan lo mismo
                          y la lista no baile cuando el sondeo cambia un estado. */}
                      <div className="mt-1 pl-[26px] flex flex-wrap items-center
                        gap-x-2 gap-y-1 min-h-[32px]">
                        <span className="text-[11px] text-[#a0a0a0] whitespace-nowrap"
                          data-testid={`tema-paginas-${tema.tema_id}`}>
                          {rangoDelTema(t, tema, fuente)}
                        </span>

                        {conMaterial && (
                          <span className="text-[11px] text-emerald-400 whitespace-nowrap"
                            data-testid={`tema-conteo-${tema.tema_id}`}>
                            {conteoDePreguntas(t, tema.preguntas || 0)}
                          </span>
                        )}

                        {fallido && (
                          <span className="text-[11px] text-red-400"
                            data-testid={`tema-fallido-${tema.tema_id}`}>
                            {t('groups.bank.temas.topic_failed')}
                          </span>
                        )}

                        {/* INFRA-2 (#363) — LA LÍNEA QUE CIERRA EL CASO.
                            Va donde iba la del fallo y NUNCA con ella (son dos
                            estados excluyentes), en el mismo amarillo del chip.
                            Dice las dos cosas que el profesor necesita: qué pasó
                            y que no hay nada que arreglar. */}
                        {sinMaterial && (
                          <span className="text-[11px] text-amber-300"
                            data-testid={`tema-sin-material-${tema.tema_id}`}>
                            {t('groups.bank.temas.no_material_line')}
                          </span>
                        )}

                        {danadas > 0 && (
                          <span className="text-[11px] text-amber-400 whitespace-nowrap"
                            data-testid={`tema-danadas-${tema.tema_id}`}>
                            {etiquetaPaginasDanadas(t, danadas)}
                          </span>
                        )}

                        {tema.a_medias && !publicado && (
                          <span className="text-[11px] text-amber-300"
                            data-testid={`tema-a-medias-${tema.tema_id}`}>
                            {t('groups.bank.temas.half_done')}
                          </span>
                        )}

                        {/* LOS DOS BOTONES DEL TEMA CON MATERIAL. Van juntos y a
                            la derecha; «Revisar preguntas» es el primario de la
                            card (es lo que el profesor viene a hacer) y «Generar
                            más» el secundario.

                            UN TEMA PUBLICADO NO LLEVA NINGUNO DE LOS DOS: es
                            sólo lectura, y su cola se abre desde el chip… no:
                            desde «Revisar preguntas», que SÍ se conserva para
                            poder RELEER lo publicado. Lo que desaparece es
                            «Generar más», que es lo que gastaría. */}
                        {conMaterial && (
                          /* LOS BOTONES PARAN EL CLICK EN SECO (firma #135).
                             «Tocar un botón JAMÁS marca la casilla» es literal:
                             sin `stopPropagation`, abrir la cola de revisión de
                             un tema lo dejaría de paso palomeado para generar —
                             o sea, para gastar. */
                          /* remates-1-3-0c (V2 de Q6): el grupo PUEDE PARTIRSE. En un
                             telefono los dos botones no cabian en la tarjeta y el de
                             "Generar mas" se salia de ella (ya en main; medido en Chrome). */
                          <div className="ml-auto flex flex-wrap justify-end items-center gap-1.5">
                            {/* 44 PT DE BLANCO TÁCTIL (T5.3, #141e). Nació con
                                `h-8` a secas —32 px— y así llevaba desde T5: el
                                barrido de contratos de T5.2 lo encontró idéntico
                                en `main`, o sea deuda heredada y no regresión,
                                y quedó anotado como cabo 11. Se le pone la MISMA
                                receta que su hermano de al lado: 44 pt de blanco
                                táctil en teléfono y altura VISUAL de 32 en
                                escritorio, para no partir la línea 2 de la card
                                (que reserva `min-h-[32px]`). */}
                            <Button size="sm" variant="outline"
                              onClick={e => { e.stopPropagation();
                                revisar(tema.tema_id); }}
                              className="border-[#3a3a3a] bg-[#161616] text-white
                                rounded-lg h-8 px-3 text-[11px] font-medium
                                min-h-[44px] sm:min-h-[32px]
                                transition-[background-color,border-color]
                                duration-150
                                hoverable:hover:bg-[#1f1f1f]
                                hoverable:hover:border-[#4a4a4a]"
                              data-testid={`tema-revisar-${tema.tema_id}`}>
                              {t('groups.bank.curacion.review_btn')}
                            </Button>
                            {!publicado && (
                              /* «GENERAR MÁS» AHORA TIENE CUERPO DE BOTÓN (T5).
                                 El texto es el copy 12 de #118, intocable; lo que
                                 cambia es la ropa. Hasta T4.5 se pintaba como un
                                 `outline` fino que en una línea llena de chips y
                                 etiquetas se leía como un chip más — o sea, como
                                 algo que informa y no como algo que se toca.
                                 Ahora lleva relleno, borde marcado y el `+`.
                                 44 pt de blanco táctil por el `min-h`; la altura
                                 VISUAL sigue en 32 para no romper la línea 2.
                                 Y la transición se declara campo por campo, nunca
                                 `all`: la lección de `glass-card`. */
                              <Button size="sm" variant="outline"
                                disabled={ocupado || demo}
                                onClick={e => { e.stopPropagation();
                                  generarMas(tema.tema_id); }}
                                className="border-[#FF6B00]/40 bg-[#FF6B00]/10
                                  text-[#FF8c3e] rounded-lg h-8 px-3 text-[11px]
                                  font-medium min-h-[44px] sm:min-h-[32px]
                                  transition-[background-color,border-color]
                                  duration-150
                                  hoverable:hover:bg-[#FF6B00]/20
                                  hoverable:hover:border-[#FF6B00]/60
                                  disabled:opacity-40"
                                data-testid={`tema-generar-mas-${tema.tema_id}`}>
                                {ocupado
                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                  : (
                                    <>
                                      <Plus className="w-3 h-3 mr-1 shrink-0"
                                        aria-hidden="true" />
                                      {t('groups.bank.temas.generate_more_btn')}
                                    </>
                                  )}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* LA MISMA BOTONERA, AL PIE (firma #135). Una lista de veinte
                  temas deja los controles de arriba fuera de pantalla justo
                  cuando el profesor acaba de terminar de marcar. */}
              <div>{acciones('abajo')}</div>
              {/* AQUÍ VIVÍA EL RECUADRO DEL PIE — RETIRADO EN LA TANDA 26b
                  (#237). Sus dos únicos textos (la marca vacía y el `detail` del
                  servidor) salen ahora por la cola de la casa, arriba y con seis
                  segundos de vida. Vivía al final de una lista que puede tener
                  veinte temas: el profesor tocaba «Generar preguntas» arriba y el
                  porqué de que no pasara nada le aparecía fuera de pantalla. */}
              </div>
            </div>
          )}

          {/* ── t7-1b (T6): "CARDS PUBLICADAS" ────────────────────────────────
              DEBAJO DE LA LISTA Y DE SU BOTONERA DE ABAJO, como seccion hermana:
              esa botonera existe para quedar al pie de la lista (firma #135), y
              meter las cards entre las dos la alejaria justo de donde el profesor
              termina de marcar. Solo con cards.

              LOS RENGLONES SON LOS DE LA LISTA DE TEMAS: la misma capa sin la
              elevacion de tocable (el renglon no se toca; se tocan sus dos
              botones), la misma linea 1 con el nombre y la misma linea 2 con el
              dato gris y los botones a la derecha. Lo UNICO distinto es que el
              nombre ENVUELVE en vez de truncarse: aqui es lo que identifica a la
              card, y el profesor lo tiene que poder leer entero. */}
          {cards.length > 0 && (
            <div className={CAPA_DE_SECCION} data-testid="temas-cards">
              {/* remates-1-3-0c (Q6): la misma capa de seccion y la misma cabecera que la de
                  temas; el conteo es el par nuevo `groups.bank.temas.cards_conteo`. */}
              <h2 className={TITULO_DE_SECCION_PLEGABLE} data-testid="temas-cards-titulo">
                {renglonPlegable(t('groups.bank.temas.cards_titulo'), cardsAbiertas,
                  setCardsAbiertas, 'temas-cards-plegar',
                  t('groups.bank.temas.cards_conteo', { count: cards.length }), 'temas-cards-conteo')}
              </h2>
              <div hidden={!cardsAbiertas} className={CUERPO_DE_SECCION} data-testid="temas-cards-cuerpo">
              <div className={RITMO_LISTA} data-testid="temas-cards-lista">
                {cards.map(card => (
                  /* t7-2a (C5, diseno 3.6; Carlos 26-sep): el renglon de una card publicada. El
                     numero a la izquierda (28 de ancho), el nombre (15 / 600) y debajo sus temas y
                     su fecha (13, gris); a la derecha "Cambiar nombre" y "Cambiar fecha" como
                     enlaces naranjas, con las MISMAS ventanas de siempre. En angosto el par de
                     enlaces baja a su propio renglon. */
                  <div key={card.id} className="flex flex-wrap items-center gap-x-3 gap-y-1"
                    style={{ background: '#181818', borderWidth: 1, borderStyle: 'solid', borderColor: '#262626', borderRadius: 12,
                      paddingTop: 14, paddingBottom: 14, paddingLeft: 16, paddingRight: 16 }}
                    data-testid={`temas-card-${card.id}`}>
                    <span className="shrink-0 self-start" style={{ width: 28, fontSize: 13, fontWeight: 700, color: '#a0a0a0', lineHeight: '22px' }}
                      data-testid={`temas-card-numero-${card.id}`}>{`#${card.numero}`}</span>
                    <div className="flex-1 min-w-[160px]">
                      <p className="text-white break-words" style={{ fontSize: 15, fontWeight: 600 }}
                        data-testid={`temas-card-nombre-${card.id}`}>
                        {card.nombre}
                      </p>
                      <p style={{ fontSize: 13, color: '#a0a0a0' }}
                        data-testid={`temas-card-info-${card.id}`}>
                        {t('doc_card.topic_count', { count: (card.temas || []).length })}
                        {card.caduca ? '. ' : null}
                        {card.caduca ? t(card.estado === 'caducada'
                          ? 'groups.bank.temas.cards_caduco'
                          : 'groups.bank.temas.cards_caduca',
                        { date: fechaDeCaducidadDe(card, i18n?.language) })
                          : null}
                      </p>
                    </div>
                    <div className="ml-auto flex flex-wrap items-center gap-x-4">
                      <button type="button" onClick={(e) => abrirCambiarNombre(card, e)}
                        className="inline-flex items-center min-h-[44px] hover:underline"
                        style={{ fontSize: 13, fontWeight: 600, color: '#FF6B00' }}
                        data-testid={`temas-card-cambiar-nombre-${card.id}`}>
                        {t('groups.bank.temas.cards_cambiar_nombre')}
                      </button>
                      <button type="button" onClick={(e) => abrirCambiarFecha(card, e)}
                        className="inline-flex items-center min-h-[44px] hover:underline"
                        style={{ fontSize: 13, fontWeight: 600, color: '#FF6B00' }}
                        data-testid={`temas-card-cambiar-fecha-${card.id}`}>
                        {t('groups.bank.temas.cards_cambiar_fecha')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── EL MODAL DE PUBLICAR (C30) ──────────────────────────────────────
          `fixed` con su propia safe area abajo, igual que los otros modales de
          esta familia (el de borrar documento y el de quitar alumno). */}
      {modalPublicar && (
        <div className="fixed top-14 inset-x-0 bottom-0 z-[60] bg-black/80 flex
          items-center justify-center p-4" data-testid="temas-publicar-modal">
          <div className="relative bg-[#111] border border-[#262626] rounded-[20px]
            p-6 sm:p-8 pb-[max(1.5rem,env(safe-area-inset-bottom,0px))] max-w-md
            w-full mx-4 shadow-2xl max-h-full overflow-y-auto"
            ref={panelPublicarRef} role="dialog" aria-modal="true" aria-labelledby="temas-publicar-titulo">
            <h3 className="text-lg font-semibold text-white mb-3" id="temas-publicar-titulo"
              data-testid="temas-publicar-titulo">{t('groups.bank.publicar.modal_title')}</h3>

            {/* ── LA LISTA ÚNICA, EN DOS BLOQUES (T5.2, firma #133 §1.4) ─────
                Arriba lo que no ha salido, premarcado; abajo lo ya publicado,
                desmarcado y con su tag. Una lista y no dos secciones con títulos:
                el profesor está eligiendo de un solo montón, y partirlo en dos
                cajas le pediría entender una taxonomía antes de poder marcar.

                RENGLONES COMPACTOS, de una línea. La versión de cards gruesas se
                probó y se descartó: con ocho temas el modal ya no cabía en un
                teléfono y había que recorrerlo para ver el total, que es justo el
                dato que la decisión necesita. */}
            <div className="space-y-0.5 mb-4" data-testid="temas-publicar-lista">
              {enElModal.map(tema => {
                const yaSalio = tema.estado === 'publicado';
                return (
                  <div key={tema.tema_id}
                    onClick={() => alternarEnModal(tema.tema_id)}
                    className="flex items-center gap-2.5 min-h-[44px] cursor-pointer
                      rounded-lg px-1 hoverable:hover:bg-white/[0.03]"
                    data-testid="temas-publicar-renglon">
                    {/* T6 / #282 — la gemela de la de arriba, misma forma. */}
                    <label className="shrink-0 inline-flex items-center cursor-pointer"
                      onClick={e => e.stopPropagation()}>
                      <input type="checkbox" className="sr-only peer"
                        checked={marcadoEnModal(tema)}
                        onChange={() => alternarEnModal(tema.tema_id)}
                        aria-labelledby={`temas-publicar-tema-${tema.tema_id}`}
                        data-testid={`temas-publicar-check-${tema.tema_id}`} />
                      <CirculoPalomita marcado={marcadoEnModal(tema)}
                        className="peer-focus-visible:ring-2 peer-focus-visible:ring-[#FF6B00]" />
                    </label>
                    {/* t7-3 remate 8 (#591, C43 y C114): el renglon parte renglon y se lee entero */}
                    <span className="text-sm text-white min-w-0 break-words flex-1"
                      id={`temas-publicar-tema-${tema.tema_id}`}
                      data-testid={`temas-publicar-tema-${tema.tema_id}`}>
                      {renglonDeTemaPublicable(t, tema.nombre, vivas(tema),
                        tema.pendientes || 0)}
                    </span>
                    {yaSalio && (
                      /* EL TAG MINIMALISTA, SIN NÚMERO. «Ya publicado #2» le
                         pediría al profesor saber qué es la card 2; el dato que
                         necesita aquí es binario —ya salió o no— y eso es lo que
                         dice. */
                      <span className="shrink-0 rounded-full border border-sky-500/40
                        bg-sky-500/10 px-2 py-0.5 text-[10px] leading-4
                        text-sky-300 whitespace-nowrap"
                        data-testid={`temas-publicar-tag-${tema.tema_id}`}>
                        {t('groups.bank.publicar.already_published')}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* EL AIRE LO PONE EL CONTENEDOR, no un margen por rama. El cuerpo
                y su línea condicional son UN bloque, así que su separación
                interna y la de abajo salen de un `space-y` y de un `mb` que no
                dependen de si la línea sale o no. */}
            <div className="space-y-2 mb-5">
            {/* post-t7-1 (A6): con CERO temas elegidos la frase no se pinta ("con 0 preguntas en total"); al elegir
                uno aparece con su numero. El resto de la ventana y su boton apagado, como antes. */}
            {marcadosEnModal.length > 0 && (
            <p className="text-sm text-[#a0a0a0]"
              data-testid="temas-publicar-cuerpo">
              {cuerpoDePublicacion(t, datos?.siguiente_publicacion || 1,
                datos?.filename || '', totalAPublicar)}
            </p>
            )}

            {/* F6 — SÓLO CUANDO DE VERDAD HAY ALGO SIN REVISAR ENTRE LO MARCADO.
                Es lo que hace honesta a la publicación directa: el profesor se
                entera de que va a mandar material que nadie leyó en el momento
                exacto en que lo va a mandar. Un aviso que saliera siempre
                dejaría de leerse a la tercera publicación. */}
            {hayPendientesMarcadas && (
              <p className="text-sm text-amber-300/90"
                data-testid="temas-publicar-sin-revisar">
                {t('groups.bank.publicar.unreviewed_line')}
              </p>
            )}
            </div>

            {/* t7-1b (T6): EL NOMBRE Y LA FECHA DE LA CARD, los dos opcionales,
                DEBAJO del texto de hoy y antes de los botones. Cada campo a todo
                el ancho debajo de su etiqueta, con su ayuda abajo. */}
            <div className="space-y-4 mb-5" data-testid="temas-publicar-campos">
              <div>
                <label htmlFor="temas-publicar-nombre" className={`block ${ETIQUETA} mb-1`}
                  data-testid="temas-publicar-nombre-etiqueta">
                  {t('groups.bank.publicar.nombre_label')}
                </label>
                <Input id="temas-publicar-nombre" type="text" maxLength={TOPE_DEL_NOMBRE}
                  value={modalPublicar.nombre || ''}
                  onChange={e => {
                    const valor = e.target.value;
                    setModalPublicar(m => (m ? { ...m, nombre: valor } : m));
                  }}
                  aria-describedby="temas-publicar-nombre-ayuda"
                  className={CAMPO} data-testid="temas-publicar-nombre" />
                <p id="temas-publicar-nombre-ayuda" className={AYUDA}
                  data-testid="temas-publicar-nombre-ayuda">
                  {t('groups.bank.publicar.nombre_ayuda')}
                </p>
              </div>
              <div>
                <label htmlFor="temas-publicar-caduca" className={`block ${ETIQUETA} mb-1`}
                  data-testid="temas-publicar-caduca-etiqueta">
                  {t('groups.bank.publicar.caduca_label')}
                </label>
                <Input id="temas-publicar-caduca" type="date" min={mananaLocal()}
                  value={modalPublicar.caduca || ''}
                  onChange={e => {
                    const valor = e.target.value;
                    setModalPublicar(m => (m ? { ...m, caduca: valor } : m));
                  }}
                  aria-describedby="temas-publicar-caduca-ayuda"
                  className={CAMPO_FECHA} data-testid="temas-publicar-caduca" />
                <p id="temas-publicar-caduca-ayuda" className={AYUDA}
                  data-testid="temas-publicar-caduca-ayuda">
                  {t('groups.bank.publicar.caduca_ayuda')}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={() => setModalPublicar(null)}
                variant="outline"
                className="flex-1 border-[#333] text-[#a0a0a0] rounded-xl
                  min-h-[44px] text-sm"
                data-testid="temas-publicar-cancelar">
                {t('common:cancel')}
              </Button>
              <Button onClick={publicar}
                disabled={publicando || totalAPublicar === 0}
                className="flex-1 bg-[#FF6B00] hover:bg-[#E05A00] text-white
                  rounded-xl min-h-[44px] text-sm disabled:opacity-40"
                data-testid="temas-publicar-confirmar">
                {publicando
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : t('groups.bank.publicar.btn')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── t7-1b (T6): LA VENTANA CHICA DE UNA CARD ──────────────────────────
          "Cambiar nombre" o "Cambiar fecha", con la misma pieza que la de
          publicar. Titulo = el texto del enlace que la abrio; etiqueta y ayuda =
          las del mismo campo en la ventana de publicar. "Quitar fecha" solo
          aparece si la card tiene fecha: sin fecha no habria nada que quitar.
          Guardar una fecha pide una fecha (quitarla es su propio boton). */}
      {ventanaDeCard && (
        <div className={VENTANA_CAPA}
          data-testid={ventanaDeCard.tipo === 'nombre'
            ? 'temas-cambiar-nombre-modal' : 'temas-cambiar-fecha-modal'}>
          <div className={VENTANA_PANEL} ref={panelCardRef} role="dialog" aria-modal="true"
            aria-labelledby="temas-card-modal-titulo">
            <h3 className={VENTANA_TITULO} id="temas-card-modal-titulo" data-testid="temas-card-modal-titulo">
              {t(ventanaDeCard.tipo === 'nombre'
                ? 'groups.bank.temas.cards_cambiar_nombre'
                : 'groups.bank.temas.cards_cambiar_fecha')}
            </h3>
            <div className="mb-5">
              <label htmlFor="temas-card-modal-campo" className={`block ${ETIQUETA} mb-1`}
                data-testid="temas-card-modal-etiqueta">
                {t(ventanaDeCard.tipo === 'nombre'
                  ? 'groups.bank.publicar.nombre_label'
                  : 'groups.bank.publicar.caduca_label')}
              </label>
              {ventanaDeCard.tipo === 'nombre' ? (
                <Input id="temas-card-modal-campo" type="text" maxLength={TOPE_DEL_NOMBRE}
                  autoFocus value={ventanaDeCard.valor}
                  onChange={e => escribirEnVentana(e.target.value)}
                  aria-describedby="temas-card-modal-ayuda"
                  className={CAMPO} data-testid="temas-cambiar-nombre-input" />
              ) : (
                <Input id="temas-card-modal-campo" type="date" min={mananaLocal()}
                  value={ventanaDeCard.valor}
                  onChange={e => escribirEnVentana(e.target.value)}
                  aria-describedby="temas-card-modal-ayuda"
                  className={CAMPO_FECHA} data-testid="temas-cambiar-fecha-input" />
              )}
              <p id="temas-card-modal-ayuda" className={AYUDA}
                data-testid="temas-card-modal-ayuda">
                {t(ventanaDeCard.tipo === 'nombre'
                  ? 'groups.bank.publicar.nombre_ayuda'
                  : 'groups.bank.publicar.caduca_ayuda')}
              </p>
              {ventanaDeCard.tipo === 'fecha' && ventanaDeCard.card.caduca && (
                <Button size="sm" variant="outline" onClick={quitarFecha}
                  disabled={guardandoCard}
                  className={`mt-3 ${BOTON_DE_RENGLON}`}
                  data-testid="temas-quitar-fecha">
                  {t('groups.bank.temas.cards_quitar_fecha')}
                </Button>
              )}
            </div>

            <div className={BOTONERA}>
              <Button onClick={() => setVentanaDeCard(null)} variant="outline"
                disabled={guardandoCard}
                className={BOTON_CANCELAR} data-testid="temas-card-cancelar">
                {t('common:cancel')}
              </Button>
              <Button onClick={guardarCard}
                disabled={guardandoCard
                  || (ventanaDeCard.tipo === 'fecha' && !ventanaDeCard.valor)}
                className={BOTON_GUARDAR} data-testid="temas-card-guardar">
                {guardandoCard
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : t('common:save')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
